/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React from 'react'
import clsx from 'clsx'
import {useTranslation} from 'react-i18next'
import {Tooltip} from 'react-tooltip'
import {LeadStatus} from 'types/enums/LeadStatus'
import {
    getSystemOnlyOpportunityStatusReason,
    isManuallySelectableOpportunityStatus,
    useLeadStatusOptions,
} from 'utils/leads/leadsUtils'

const SYSTEM_STATUS_LOCKED_TOOLTIP_ID = 'opportunity-system-status-locked-tooltip'

interface UiLeadStatusButtonGroupProps {
    value: LeadStatus | null
    onChange: (value: LeadStatus) => void
    disabled?: boolean
    className?: string
    isOpportunity?: boolean
}

const UiLeadStatusButtonGroup: React.FC<UiLeadStatusButtonGroupProps> = ({
    value,
    onChange,
    disabled,
    className,
    isOpportunity = false,
}) => {
    const { t } = useTranslation()
    const { options } = useLeadStatusOptions(isOpportunity)

    const getStatusColors = (status: LeadStatus, isActive: boolean) => {
        if (!isActive) {
            return 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
        }
        switch (status) {
            case LeadStatus.New:
                return 'bg-red-600 text-white dark:bg-red-700'
            case LeadStatus.Contacted:
                return 'bg-blue-500 text-white dark:bg-blue-600'
			case LeadStatus.ContactAttempt:
				return 'bg-cyan-600 text-white dark:bg-cyan-700 dark:text-white'
			case LeadStatus.Disqualified:
				return 'bg-indigo-600 text-white dark:bg-indigo-700 dark:text-white'
			case LeadStatus.OpportunityCreated:
				return 'bg-orange-500 text-white dark:bg-orange-600 dark:text-white'
            case LeadStatus.NewInquiry:
                return 'bg-purple-600 text-white dark:bg-purple-700'
            case LeadStatus.IndicativeOffer:
                return 'bg-cyan-600 text-white dark:bg-cyan-700'
            case LeadStatus.Quotation:
                return 'bg-indigo-600 text-white dark:bg-indigo-700'
            case LeadStatus.Acceptance:
                return 'bg-green-600 text-white dark:bg-green-700'
			case LeadStatus.Finished:
                return 'bg-blue-600 text-blue-100 dark:bg-blue-700'
            case LeadStatus.OnHold:
                return 'bg-amber-600 text-white dark:bg-amber-700'
            case LeadStatus.Rejection:
                return 'bg-red-700 text-white dark:bg-red-800'
            default:
                return 'bg-red-600 text-white dark:bg-red-700'
        }
    }

    const hasLockedOptions = isOpportunity && options.some(
        (option) => !isManuallySelectableOpportunityStatus(option.value, value)
    )

    return (
        <div className={clsx('UiLeadStatusButtonGroup flex items-center', className)}>
            <div className="flex rounded-lg overflow-hidden">
                {options.map((option, index) => {
                    const isActive = value === option.value
                    const isSystemLocked =
                        isOpportunity &&
                        !isManuallySelectableOpportunityStatus(option.value, value)
                    const lockedReason = isSystemLocked
                        ? getSystemOnlyOpportunityStatusReason(t, option.value)
                        : undefined
                    const isButtonDisabled = disabled || isSystemLocked
                    return (
                        <span
                            key={option.value}
                            className="inline-flex"
                            data-tooltip-id={
                                isSystemLocked ? SYSTEM_STATUS_LOCKED_TOOLTIP_ID : undefined
                            }
                            data-tooltip-content={lockedReason}
                        >
                            <button
                                type="button"
                                disabled={isButtonDisabled}
                                onClick={() => onChange(option.value)}
                                className={clsx(
                                    'px-3 py-1.5 text-xs font-medium transition-colors',
                                    getStatusColors(option.value, isActive),
                                    isButtonDisabled && 'opacity-50 cursor-not-allowed',
                                    index === 0 && 'rounded-l-lg',
                                    index === options.length - 1 && 'rounded-r-lg'
                                )}
                            >
                                {option.label}
                            </button>
                        </span>
                    )
                })}
            </div>
            {hasLockedOptions && (
                <Tooltip id={SYSTEM_STATUS_LOCKED_TOOLTIP_ID} style={{ zIndex: 99999 }} />
            )}
        </div>
    )
}

export default UiLeadStatusButtonGroup
